import React, { useEffect, useState } from "react";
import imageCompression from "browser-image-compression";
import Axios from "../../axios/axios";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import AlertModal from "../../actions/AlertModal";
import { FaEye, FaEyeSlash } from "react-icons/fa";

function SupplierSignup() {
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [currentUploadField, setCurrentUploadField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const [modalMessage, setModalMessage] = useState("");

  const [formData, setFormData] = useState({
    // Step 1: Basic Information
    supplierName: "",
    companyName: "",
    emailAddress: "",
    phoneNumber: "",
    preferredLanguage: "English",
    productCategory: "",

    // Step 2: Address & Legal Information
    emirate: "",
    city: "",
    poBox: "",
    address: "",
    deliveryAddress: "",
    gstNumber: "",
    tradeLicenseNumber: "",
    vatRegistrationNumber: "",

    // Files
    image: { title: "", url: "" },
    tradeLicense: { title: "", url: "" },
    vatCertificate: { title: "", url: "" },

    // Step 3: Bank & Account Information
    accountNumber: "",
    ifscCode: "",
    iban: "",
    swiftCode: "",
    balance: "0",

    // Step 4: Account Credentials
    userName: "",
    password: "",
    confirmPassword: "",
  });

  const checkEmailExists = async (email) => {
    try {
      const response = await Axios.post("/check-email", { email });
      return response.data.exists;
    } catch (error) {
      console.error("Error checking email:", error);
      return false;
    }
  };

  const uploadFileHandler = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    const options = {
      maxSizeMB: 0.2,
      maxWidthOrHeight: 800,
      useWebWorker: true,
    };

    try {
      setUploading(true);
      setCurrentUploadField(fieldName);

      const compressedFile = file.type.startsWith("image/")
        ? await imageCompression(file, options)
        : file;

      const uploadFormData = new FormData();
      uploadFormData.append("images", compressedFile);

      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };

      const { data } = await Axios.post(
        "/upload-multiple",
        uploadFormData,
        config
      );

      setFormData((prev) => ({
        ...prev,
        [fieldName]: {
          title: file.name,
          url: data.fileUrls[0],
        },
      }));
    } catch (error) {
      console.error("Upload error:", error);
      // Handle error appropriately
    } finally {
      setUploading(false);
      setCurrentUploadField(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneNumberChange = (value) => {
    setPhoneNumber(value);
    setFormData((prev) => ({
      ...prev,
      phoneNumber: value,
    }));
  };

  const nextStep = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step === 3 && !validateStep3()) return;
    setStep((prev) => prev + 1);
  };

  const prevStep = () => setStep((prev) => prev - 1);

  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   // Validate password match
  //   if (formData.password !== formData.confirmPassword) {
  //     alert("Passwords don't match!");
  //     return;
  //   }

  //   setLoading(true);

  //   try {
  //     const response = await Axios.post('/supplier/create', formData);
  //     console.log('Signup successful:', response.data);
  //     // Redirect or show success message
  //   } catch (error) {
  //     console.error('Signup error:', error.response?.data || error.message);
  //     // Handle error appropriately
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep4()) return;

    // Validate password match
    if (formData.password !== formData.confirmPassword) {
      alert("Passwords don't match!");
      return;
    }

    // Check if email error exists
    if (emailError) {
      alert("Please fix the email error before submitting");
      return;
    }

    setLoading(true);

    try {
      const response = await Axios.post("/supplier/create", formData);
      console.log("Signup successful:", response.data);
      // Redirect or show success message
      setModalMessage(
        <>
          Your request has been submitted successfully.
          <br />
          You’ll be contacted by the admin once approved. Please check your
          email for further updates.
        </>
      );

      setIsModalOpen(true);
    } catch (error) {
      console.error("Signup error:", error.response?.data || error.message);
      // Handle error appropriately
    } finally {
      setLoading(false);
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.supplierName.trim())
      newErrors.supplierName = "Please fill this field. This is mandatory";
    if (!formData.companyName.trim())
      newErrors.companyName = "Please fill this field. This is mandatory";
    if (!formData.emailAddress.trim())
      newErrors.emailAddress = "Please fill this field. This is mandatory";
    if (!phoneNumber)
      newErrors.phoneNumber = "Please fill this field. This is mandatory";
    if (!formData.preferredLanguage.trim())
      newErrors.preferredLanguage = "Please fill this field. This is mandatory";
    if (!formData.productCategory.trim())
      newErrors.productCategory = "Please fill this field. This is mandatory";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.emirate.trim())
      newErrors.emirate = "Please fill this field. This is mandatory";
    if (!formData.city.trim())
      newErrors.city = "Please fill this field. This is mandatory";
    if (!formData.address.trim())
      newErrors.address = "Please fill this field. This is mandatory";
    if (!formData.gstNumber.trim())
      newErrors.gstNumber = "Please fill this field. This is mandatory";
    if (!formData.tradeLicenseNumber.trim())
      newErrors.tradeLicenseNumber =
        "Please fill this field. This is mandatory";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep4 = () => {
    const newErrors = {};

    if (!formData.userName.trim())
      newErrors.userName = "Please fill this field. This is mandatory";
    if (!formData.password.trim())
      newErrors.password = "Please fill this field. This is mandatory";
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = "Please fill this field. This is mandatory";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};

    if (!formData.accountNumber?.trim()) {
      newErrors.accountNumber = "Account number is required";
    }

    if (!formData.ifscCode?.trim()) {
      newErrors.ifscCode = "IFSC code is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <div className="container signupForm">
      <div
        className="row justify-content-center"
        style={{ marginTop: "150px" }}
      >
        <div className="col-10 col-sm-9 col-md-7 col-lg-6 col-xl-6 text-center p-0 mb-2">
          <div className="card px-0 pt-4 pb-0 mt-3 mb-3">
            <h3>SUPPLIER SIGNUP</h3>
            <p>Register your business to start selling on our platform</p>
            <form id="msform" onSubmit={handleSubmit}>
              <ul id="progressbar">
                <li className={step >= 1 ? "active" : ""} id="account">
                  <span className="lightGrey">Step 1</span>
                  <br />
                  <span className="stepHead">Basic Information</span>
                </li>
                <li className={step >= 2 ? "active" : ""} id="personal">
                  <span className="lightGrey">Step 2</span>
                  <br />
                  <span className="stepHead">Legal & Address</span>
                </li>
                <li className={step >= 3 ? "active" : ""} id="payment">
                  <span className="lightGrey">Step 3</span>
                  <br />
                  <span className="stepHead">Bank Details</span>
                </li>
                <li className={step >= 4 ? "active" : ""} id="credentials">
                  <span className="lightGrey">Step 4</span>
                  <br />
                  <span className="stepHead">Account Credentials</span>
                </li>
              </ul>
              <div className="progress">
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${(step / 4) * 100}%` }}
                  aria-valuenow={(step / 4) * 100}
                  aria-valuemin="0"
                  aria-valuemax="100"
                ></div>
              </div>
              <br />

              {/* Step 1: Basic Information */}
              {step === 1 && (
                <fieldset>
                  <div className="form-card">
                    <div className="row mt_20">
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="supplierName">Supplier Name*</label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.supplierName ? "is-invalid" : ""
                            }`}
                            id="supplierName"
                            name="supplierName"
                            value={formData.supplierName}
                            onChange={handleChange}
                            placeholder="Supplier Name"
                            required
                          />
                          {errors.supplierName && (
                            <div className="invalid-feedback">
                              {errors.supplierName}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="companyName">Company Name*</label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.companyName ? "is-invalid" : ""
                            }`}
                            id="companyName"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            placeholder="Company Name"
                            required
                          />
                          {errors.companyName && (
                            <div className="invalid-feedback">
                              {errors.companyName}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row mt_20">
                      {/* <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="emailAddress">Email Address*</label>
                          <input 
                            type="email" 
                            className="form-control" 
                            id="emailAddress" 
                            name="emailAddress"
                            value={formData.emailAddress}
                            onChange={handleChange}
                            placeholder="Email Address"
                            required
                          />
                        </div>
                      </div> */}

                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="emailAddress">Email Address*</label>
                          <input
                            type="email"
                            className={`form-control ${
                              emailError ? "is-invalid" : ""
                            }`}
                            id="emailAddress"
                            name="emailAddress"
                            value={formData.emailAddress}
                            onChange={handleChange}
                            onBlur={async (e) => {
                              const email = e.target.value;

                              // Simple format validation using regex
                              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                              if (!emailRegex.test(email)) {
                                setEmailError(
                                  "Please enter a valid email address"
                                );
                                return;
                              }

                              // Then check if already exists
                              const exists = await checkEmailExists(email);
                              if (exists) {
                                setEmailError(
                                  "This email is already registered"
                                );
                              } else {
                                setEmailError("");
                              }
                            }}
                            placeholder="Email Address"
                            required
                          />
                          {emailError && (
                            <div className="invalid-feedback d-block">
                              {emailError}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="phoneNumber">Phone Number*</label>
                          <PhoneInput
                            international
                            defaultCountry="AE" // UAE as default country
                            value={phoneNumber}
                            onChange={handlePhoneNumberChange}
                            className={`form-control ${
                              errors.phoneNumber ? "is-invalid" : ""
                            }`}
                            id="phoneNumber"
                            placeholder="Enter phone number"
                            required
                          />
                          {errors.phoneNumber && (
                            <div className="invalid-feedback">
                              {errors.phoneNumber}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row mt_20">
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="preferredLanguage">
                            Preferred Language*
                          </label>
                          <select
                            className={`form-control selectDropDown  ${
                              errors.preferredLanguage ? "is-invalid" : ""
                            }`}
                            id="preferredLanguage"
                            name="preferredLanguage"
                            value={formData.preferredLanguage}
                            onChange={handleChange}
                            required
                          >
                            <option value="English">English</option>
                            <option value="Arabic">Arabic</option>
                          </select>
                          {errors.productCategory && (
                            <div className="invalid-feedback d-block">
                              {errors.productCategory}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="productCategory">
                            Product Category*
                          </label>
                          <select
                            className={`form-control selectDropDown ${
                              errors.productCategory ? "is-invalid" : ""
                            }`}
                            id="productCategory"
                            name="productCategory"
                            value={formData.productCategory}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select Category</option>
                            <option value="Auto-Spare">Auto-Spare</option>
                            <option value="Engineering">Engineering</option>
                            <option value="Building">Building</option>
                            <option value="Rental">Rental</option>
                          </select>

                          {errors.productCategory && (
                            <div className="invalid-feedback d-block">
                              {errors.productCategory}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row mt_20">
                      <div className="col-md-12 form_Content">
                        <div className="form-group">
                          <label htmlFor="supplierImage">Supplier Image</label>
                          <input
                            type="file"
                            className="form-control"
                            id="supplierImage"
                            onChange={(e) => uploadFileHandler(e, "image")}
                            accept="image/*"
                          />
                          {uploading && currentUploadField === "image" && (
                            <span>Uploading...</span>
                          )}
                          {formData.image.url && (
                            <div className="mt-2">
                              <img
                                src={formData.image.url}
                                alt="Preview"
                                style={{
                                  maxWidth: "100px",
                                  maxHeight: "100px",
                                }}
                              />
                              <p>{formData.image.title}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <input
                    type="button"
                    name="next"
                    className="next action-button formContinueBtn"
                    value="Continue"
                    onClick={nextStep}
                    disabled={uploading}
                  />
                </fieldset>
              )}

              {/* Step 2: Legal & Address Information */}
              {step === 2 && (
                <fieldset>
                  <div className="form-card">
                    <div className="row mt_20">
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="emirate">Emirate*</label>
                          <select
                            className={`form-control selectDropDown ${
                              errors.emirate ? "is-invalid" : ""
                            }`}
                            id="emirate"
                            name="emirate"
                            value={formData.emirate}
                            onChange={handleChange}
                          >
                            <option value="">Select Emirate</option>
                            <option value="Abu Dhabi">Abu Dhabi</option>
                            <option value="Dubai">Dubai</option>
                            ...
                          </select>
                          {errors.emirate && (
                            <div className="invalid-feedback">
                              {errors.emirate}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="city">City*</label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.city ? "is-invalid" : ""
                            }`}
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            placeholder="City"
                          />
                          {errors.city && (
                            <div className="invalid-feedback">
                              {errors.city}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row mt_20">
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="poBox">PO Box</label>
                          <input
                            type="text"
                            className="form-control"
                            id="poBox"
                            name="poBox"
                            value={formData.poBox}
                            onChange={handleChange}
                            placeholder="PO Box"
                          />
                        </div>
                      </div>
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="address">Address*</label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.address ? "is-invalid" : ""
                            }`}
                            id="address"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            placeholder="Address"
                            required
                          />
                          {errors.address && (
                            <div className="invalid-feedback">
                              {errors.address}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row mt_20">
                      <div className="col-md-12 form_Content">
                        <div className="form-group">
                          <label htmlFor="deliveryAddress">
                            Delivery Address
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="deliveryAddress"
                            name="deliveryAddress"
                            value={formData.deliveryAddress}
                            onChange={handleChange}
                            placeholder="Delivery Address"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row mt_20">
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="gstNumber">GST Number*</label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.gstNumber ? "is-invalid" : ""
                            }`}
                            id="gstNumber"
                            name="gstNumber"
                            value={formData.gstNumber}
                            onChange={handleChange}
                            placeholder="GST Number"
                          />
                          {errors.gstNumber && (
                            <div className="invalid-feedback">
                              {errors.gstNumber}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="tradeLicenseNumber">
                            Trade License Number*
                          </label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.tradeLicenseNumber ? "is-invalid" : ""
                            }`}
                            id="tradeLicenseNumber"
                            name="tradeLicenseNumber"
                            value={formData.tradeLicenseNumber}
                            onChange={handleChange}
                            placeholder="Trade License Number"
                          />
                          {errors.tradeLicenseNumber && (
                            <div className="invalid-feedback">
                              {errors.tradeLicenseNumber}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row mt_20">
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="vatRegistrationNumber">
                            VAT Registration Number
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="vatRegistrationNumber"
                            name="vatRegistrationNumber"
                            value={formData.vatRegistrationNumber}
                            onChange={handleChange}
                            placeholder="VAT Registration Number"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row mt_20">
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="tradeLicense">
                            Trade License Document
                          </label>
                          <input
                            type="file"
                            className="form-control"
                            id="tradeLicense"
                            onChange={(e) =>
                              uploadFileHandler(e, "tradeLicense")
                            }
                            required
                          />
                          {uploading &&
                            currentUploadField === "tradeLicense" && (
                              <span>Uploading...</span>
                            )}
                          {formData.tradeLicense.url && (
                            <div className="mt-2">
                              <a
                                href={formData.tradeLicense.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View Document
                              </a>
                              <p>{formData.tradeLicense.title}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="vatCertificate">
                            VAT Certificate (if applicable)
                          </label>
                          <input
                            type="file"
                            className="form-control"
                            id="vatCertificate"
                            onChange={(e) =>
                              uploadFileHandler(e, "vatCertificate")
                            }
                          />
                          {uploading &&
                            currentUploadField === "vatCertificate" && (
                              <span>Uploading...</span>
                            )}
                          {formData.vatCertificate.url && (
                            <div className="mt-2">
                              <a
                                href={formData.vatCertificate.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View Document
                              </a>
                              <p>{formData.vatCertificate.title}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between">
                    <input
                      type="button"
                      name="previous"
                      className="previous action-button"
                      value="Previous"
                      onClick={prevStep}
                      disabled={uploading}
                    />
                    <input
                      type="button"
                      name="next"
                      className="next action-button"
                      value="Continue"
                      onClick={nextStep}
                      disabled={uploading}
                    />
                  </div>
                </fieldset>
              )}

              {/* Step 3: Bank Details */}
              {step === 3 && (
                <fieldset>
                  <div className="form-card">
                    <div className="row mt_20">
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="accountNumber">Account Number*</label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.accountNumber ? "is-invalid" : ""
                            }`}
                            id="accountNumber"
                            name="accountNumber"
                            value={formData.accountNumber}
                            onChange={handleChange}
                            placeholder="Account Number"
                            required
                          />
                          {errors.accountNumber && (
                            <div className="invalid-feedback d-block">
                              {errors.accountNumber}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="ifscCode">IFSC Code*</label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.ifscCode ? "is-invalid" : ""
                            }`}
                            id="ifscCode"
                            name="ifscCode"
                            value={formData.ifscCode}
                            onChange={handleChange}
                            placeholder="IFSC Code"
                            required
                          />
                          {errors.ifscCode && (
                            <div className="invalid-feedback d-block">
                              {errors.ifscCode}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row mt_20">
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="iban">IBAN</label>
                          <input
                            type="text"
                            className="form-control"
                            id="iban"
                            name="iban"
                            value={formData.iban}
                            onChange={handleChange}
                            placeholder="IBAN"
                          />
                        </div>
                      </div>
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="swiftCode">SWIFT Code</label>
                          <input
                            type="text"
                            className="form-control"
                            id="swiftCode"
                            name="swiftCode"
                            value={formData.swiftCode}
                            onChange={handleChange}
                            placeholder="SWIFT Code"
                          />
                        </div>
                      </div>
                      {/* <div className="col-md-6 form_Content">
            <div className="form-group">
              <label htmlFor="balance">Initial Balance (AED)*</label>
              <input 
                type="number" 
                className="form-control" 
                id="balance" 
                name="balance"
                value={formData.balance}
                onChange={handleChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                required
              />
            </div>
          </div> */}
                    </div>
                  </div>
                  <div className="d-flex justify-content-between">
                    <input
                      type="button"
                      name="previous"
                      className="previous action-button"
                      value="Previous"
                      onClick={prevStep}
                      disabled={uploading}
                    />
                    <input
                      type="button"
                      name="next"
                      className="next action-button"
                      value="Continue"
                      onClick={nextStep}
                      disabled={uploading}
                    />
                  </div>
                </fieldset>
              )}

              {/* Step 4: Account Credentials */}
              {step === 4 && (
                <fieldset>
                  <div className="form-card">
                    <div className="row mt_20">
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="userName">Username*</label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.userName ? "is-invalid" : ""
                            }`}
                            id="userName"
                            name="userName"
                            value={formData.userName}
                            onChange={handleChange}
                            placeholder="Username"
                          />
                          {errors.userName && (
                            <div className="invalid-feedback">
                              {errors.userName}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6 form_Content">
                        <div className="form-group">
                          <label htmlFor="password">Password*</label>
                          <input
                            type="password"
                            className={`form-control ${
                              errors.password ? "is-invalid" : ""
                            }`}
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Password"
                          />
                          {errors.password && (
                            <div className="invalid-feedback">
                              {errors.password}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row mt_20">
                      <div className="col-md-6 form_Content">
                        <div className="form-group position-relative">
                          <label htmlFor="confirmPassword">
                            Confirm Password*
                          </label>
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            className={`form-control ${
                              errors.confirmPassword ? "is-invalid" : ""
                            }`}
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            placeholder="Confirm Password"
                          />
                          {errors.confirmPassword && (
                            <div className="invalid-feedback">
                              {errors.confirmPassword}
                            </div>
                          )}

                          <span
                            onClick={() =>
                              setShowConfirmPassword((prev) => !prev)
                            }
                            style={{
                              position: "absolute",
                              right: "10px",
                              top: "38px",
                              cursor: "pointer",
                              color: "#999",
                            }}
                          >
                            {showConfirmPassword ? <FaEye size={20} /> : <FaEyeSlash size={20} />}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between">
                    <input
                      type="button"
                      name="previous"
                      className="previous action-button"
                      value="Previous"
                      onClick={prevStep}
                      disabled={uploading || loading}
                    />
                    <button
                      type="submit"
                      className="btn submitFormBtn"
                      disabled={uploading || loading}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Processing...
                        </>
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                </fieldset>
              )}
            </form>
          </div>
        </div>
      </div>
      <AlertModal
        isOpen={isModalOpen}
        message={modalMessage}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}

export default SupplierSignup;
